// shared-lib/group-customization/resolveGroup.js
//
// Enforce a fixture group's links in WORLD space — the one call every commit
// site makes (2D/3D move, model swap, run re-solve, scan post-processing,
// fix-all) so a link is a constraint everywhere, not only in the Group
// Editor. See reference-docs/layout-relationships/overview.md §3.2.
//
// Two things happen, in order:
//
//   1. Geometry-wins reconcile. A link written before enforcement existed may
//      carry an offset that no longer matches the objects (the drift check in
//      snapshot.js fired only at editor save; #1502 moved 16 faucets without
//      their links). The FIRST time an `rp` link is resolved — no
//      `verifiedAt` stamp — the actual geometry wins: the stored offset is
//      rewritten from the objects and the link is stamped. Objects are never
//      moved to satisfy a stale record. Every link written at a build site
//      is born verified because it was just computed from that geometry.
//
//   2. Solve. Pinned members (the ones the caller moved) seed the solver;
//      everything else follows its links.
//
//   3. Relax, in the smallest form of §3.5. A link the current geometry
//      cannot satisfy together with the others (a copy whose sconce pairs
//      were hung asymmetrically, a rail whose members drifted apart) is
//      NOT a reason to leave the whole group unenforced: the conflicting
//      link is set aside, reported in `unsatisfied`, and the rest are
//      solved. The link stays in the data — it is the designer's statement
//      — and the editor can show it as not met. A link naming an object
//      that no longer exists is excluded the same way (`dangling`).
//      Bounded by the link count; a group that still conflicts with every
//      symmetric link relaxed comes back `rejected: true`, unchanged.

const { buildAnchorLocal } = require('./anchorPose');
const {
  localToWorld,
  computeRpOffset,
  computeRpRotYDiff,
} = require('./transforms');
const { solveConstraints, TRANSLATION_EPS } = require('./constraintSolver');
const { canonicalType } = require('./linkSemantics');
const { pickAnchor } = require('./anchorTypes');
const { wallFaceDzInAnchorFrame } = require('./mountRules');
const { linkRefs } = require('./mergeLinks');

const ROT_EPS = 1e-3;

/**
 * Rewrite never-verified rp offsets from the current geometry and stamp
 * every rp link `verifiedAt`. Pure.
 */
function reconcileRpLinks(links, localById, now) {
  const warnings = [];
  const out = (links || []).map((l) => {
    if (!l || canonicalType(l.type) !== 'rp' || l.verifiedAt) return l;
    const s = localById.get(l.sourceId);
    const t = localById.get(l.targetId);
    if (!s || !t) return l;
    const mode = l.rotationMode || 'copy';
    const actual = computeRpOffset(s, t, mode);
    const stored = l.offset || { dx: 0, dy: 0, dz: 0 };
    const delta = Math.hypot(
      (actual.dx || 0) - (stored.dx || 0),
      (actual.dy || 0) - (stored.dy || 0),
      (actual.dz || 0) - (stored.dz || 0)
    );
    const next = { ...l, verifiedAt: now };
    let drifted = false;
    if (delta > TRANSLATION_EPS) {
      next.offset = actual;
      drifted = true;
    }
    if (mode === 'copy') {
      // Rotation drift must adopt too (#1534): stamping `verifiedAt` while
      // only the rotation drifted baked the stale `rotYDiff` in as verified,
      // and the next solve then rotated the target to the stale angle.
      const actualRot = computeRpRotYDiff(s, t);
      const norm = (a) => Math.atan2(Math.sin(a), Math.cos(a));
      const dRot = Math.abs(norm(actualRot - (l.rotYDiff || 0)));
      if (drifted || dRot > ROT_EPS) {
        next.rotYDiff = actualRot;
        if (dRot > ROT_EPS) drifted = true;
      }
    }
    if (drifted) warnings.push({ kind: 'reconciled', linkId: l.id, delta });
    return next;
  });
  return { links: out, warnings };
}

/** `on-wall` resolver over real walls, in the anchor's frame. */
function wallPlaneInAnchorFrame(wallRef, child, walls, anchor) {
  const wall = (walls || []).find((w) => w?.id === wallRef?.id);
  const dz = wallFaceDzInAnchorFrame(wall, anchor, child?.dimensions?.z);
  if (dz == null) return null;
  return { dz, rotYRel: 0 };
}

/**
 * @param {Object[]} groupObjects — world-space objects of ONE fixture group
 * @param {Object[]} links — the group's links
 * @param {Object} [opts]
 * @param {Set<string>} [opts.pinnedIds] — members the caller moved; they
 *   stay where they are and pull the rest. Empty → the anchor is pinned.
 * @param {Object[]} [opts.walls] — room walls, for `on-wall` links.
 * @param {Function} [opts.physicalAxes] — extra decided axes (editor snaps).
 * @param {Map} [opts.fixedNodes] — refKey → anchor-local pose for openings /
 *   walls named by center-on / frame links.
 * @param {string} [opts.now] — ISO timestamp for `verifiedAt` (tests).
 * @returns {{ objects: Object[], links: Object[], moved: string[],
 *   reconciled: string[], unsatisfied: {linkId, reason}[],
 *   warnings: Object[], rejected: boolean }}
 */
function resolveGroup(groupObjects, links, opts = {}) {
  const objects = Array.isArray(groupObjects) ? groupObjects : [];
  const inputLinks = Array.isArray(links) ? links : [];
  const unchanged = (extra = {}) => ({
    objects,
    links: inputLinks,
    moved: [],
    reconciled: [],
    unsatisfied: [],
    warnings: [],
    rejected: false,
    ...extra,
  });
  const anchor = pickAnchor(objects);
  if (!anchor || inputLinks.length === 0) return unchanged();

  const locals = [];
  for (const o of objects) {
    const local = buildAnchorLocal(o, anchor);
    if (!local) continue;
    locals.push({
      ...local,
      dimensions: o.dimensions,
      parentWallIds: o.parentWallIds,
      parents: o.parents,
      original: o,
    });
  }
  const localById = new Map(locals.map((c) => [c.id, c]));
  const now = opts.now || new Date().toISOString();

  const { links: rLinks, warnings } = reconcileRpLinks(
    inputLinks,
    localById,
    now
  );
  const reconciled = warnings.map((w) => w.linkId);

  // Links naming an object that is not in the group cannot be evaluated.
  const unsatisfied = [];
  const live = [];
  for (const l of rLinks) {
    const refs = linkRefs(l);
    // Zero FREE refs is not dangling (#1538/L5): the generator's
    // sinkOnWindow center-on names only fixed members (appliance + opening)
    // and is valid — enforced at run level, ignored here. Dangling means a
    // named object is GONE.
    if (refs.every((id) => localById.has(id))) live.push(l);
    else unsatisfied.push({ linkId: l.id, reason: 'dangling' });
  }

  const hasFixedNodes =
    opts.fixedNodes instanceof Map && opts.fixedNodes.size > 0;
  const requested =
    opts.pinnedIds &&
    (opts.pinnedIds.size || (opts.allowNoPins && hasFixedNodes))
      ? opts.pinnedIds
      : new Set([anchor.id]);
  const pins = new Map();
  for (const id of requested) if (localById.has(id)) pins.set(id, {});
  // With nothing pinned, the anchor holds — unless fixed nodes seed the solve.
  if (pins.size === 0 && !hasFixedNodes) pins.set(anchor.id, {});

  const solverOpts = {
    physicalAxes: opts.physicalAxes,
    fixedNodes: opts.fixedNodes,
    projectAll: true,
    wallPlaneFor: opts.walls
      ? (ref, child) => wallPlaneInAnchorFrame(ref, child, opts.walls, anchor)
      : undefined,
  };
  let active = live;
  let res = solveConstraints(locals, active, pins, solverOpts);
  // Relax: set aside the links the conflicts name, one round at a time.
  for (let round = 0; res.reject && round < live.length; round++) {
    const bad = new Set(res.conflicts.map((c) => c.linkId));
    const next = active.filter((l) => !bad.has(l.id));
    if (next.length === active.length) break; // conflict names nothing we hold
    for (const l of active) {
      if (bad.has(l.id)) unsatisfied.push({ linkId: l.id, reason: 'conflict' });
    }
    active = next;
    res = solveConstraints(locals, active, pins, solverOpts);
  }

  if (res.reject) {
    return unchanged({
      links: rLinks,
      reconciled,
      unsatisfied,
      warnings: [...warnings, { kind: 'rejected', conflicts: res.conflicts }],
      rejected: true,
    });
  }

  const moved = [];
  const solvedById = new Map(res.children.map((c) => [c.id, c]));
  const outObjects = objects.map((o) => {
    const c = solvedById.get(o.id);
    if (!c) return o;
    const center = localToWorld({ dx: c.dx, dy: c.dy, dz: c.dz }, anchor);
    const rotY = (anchor.rotation?.y || 0) + (c.rotYRel || 0);
    const cur = o.center || { x: 0, y: 0, z: 0 };
    const same =
      Math.abs(center.x - (cur.x || 0)) <= TRANSLATION_EPS &&
      Math.abs(center.y - (cur.y || 0)) <= TRANSLATION_EPS &&
      Math.abs(center.z - (cur.z || 0)) <= TRANSLATION_EPS &&
      Math.abs(rotY - (o.rotation?.y || 0)) <= ROT_EPS;
    if (same) return o;
    moved.push(o.id);
    return {
      ...o,
      center,
      rotation: { x: 0, y: rotY, z: 0 },
      lastModified: now,
    };
  });

  const updates = new Map(res.suppressedRpUpdates.map((u) => [u.linkId, u]));
  const outLinks = rLinks.map((l) => {
    const u = updates.get(l.id);
    if (!u) return l;
    const next = { ...l, offset: u.offset };
    if (u.rotYDiff != null) next.rotYDiff = u.rotYDiff;
    return next;
  });

  return {
    objects: outObjects,
    links: outLinks,
    moved,
    reconciled,
    unsatisfied,
    warnings,
    rejected: false,
  };
}

module.exports = { resolveGroup, reconcileRpLinks };
